const DEFAULT_BUFFER_HOURS = 1;

const parseHHMM = (s) => {
  if (typeof s !== 'string') return null;
  const [h, m] = s.split(':').map(Number);
  if (Number.isNaN(h)) return null;
  return h + (m || 0) / 60;
};

// shape_of_day start/end arrive as numbers or "HH:MM" strings (the dashboard's
// CSV parser formats them to "HH:MM"). Resolve either to decimal hours.
const toHours = (v) => {
  if (typeof v === 'number') return Number.isNaN(v) ? null : v;
  if (typeof v === 'string') return v === '' ? null : (v.includes(':') ? parseHHMM(v) : Number(v));
  return null;
};

// Resolve a (possibly composite) shape_of_day_type to a single role, using the
// same priority ScheduleGrid uses to group open shifts (Waiter > Kitchen > first).
const resolveShapeRole = (sod) => {
  if (typeof sod !== 'string' || !sod) return null;
  if (sod.includes('+')) {
    const parts = sod.split('+').map(p => p.trim());
    if (parts.includes('Waiter')) return 'Waiter';
    if (parts.includes('Kitchen')) return 'Kitchen';
    return parts[0];
  }
  return sod;
};

// "open" = earliest start, "close" = latest end — scoped to shape_of_day rows for
// the shift's day AND its shape_of_day_type, so a Kitchen "open" tracks when the
// kitchen opens, not the venue's earliest headcount. Falls back to all roles when
// `role` is null.
const resolveDayBound = (which, shift, shapeOfDay, role) => {
  if (!Array.isArray(shapeOfDay) || shapeOfDay.length === 0) return null;
  const rows = shapeOfDay.filter(
    r => r.day === shift.day && (role == null || resolveShapeRole(r.shape_of_day_type) === role),
  );
  if (rows.length === 0) return null;
  const values = rows
    .map(r => toHours(which === 'open' ? r.start : r.end))
    .filter(v => v != null && !Number.isNaN(v));
  if (values.length === 0) return null;
  return which === 'open' ? Math.min(...values) : Math.max(...values);
};

const resolveBound = (bound, shift, shapeOfDay, role) => {
  if (bound === 'open' || bound === 'close') return resolveDayBound(bound, shift, shapeOfDay, role);
  if (Array.isArray(bound)) {
    const [h, m] = bound;
    if (typeof h !== 'number') return null;
    return h + (m || 0) / 60;
  }
  if (typeof bound === 'string') return parseHHMM(bound);
  if (typeof bound === 'number') return bound;
  return null;
};

const getIndividualWindows = (member, dayLower) => {
  const ideal = member?.Ideal_Shifts;
  if (!ideal || typeof ideal !== 'object') return null;
  // Ideal_Shifts day keys arrive in either casing: the dashboard defaults use
  // lowercase ("monday"), while backend/test requests use uppercase ("MONDAY").
  // Resolve the key case-insensitively so person-level windows always win over role.
  const key = Object.keys(ideal).find(k => k.toLowerCase() === dayLower);
  const entries = key != null ? ideal[key] : undefined;
  if (!Array.isArray(entries) || entries.length === 0) return null;
  return entries.map(([s, e]) => [s, e]);
};

const getRoleWindows = (config, role, dayUpper) => {
  const entries =
    config?.ROLES?.[role]?.SHIFT_LENGTH?.IDEAL_SETTINGS?.[dayUpper]?.IDEAL_SHIFTS;
  if (!Array.isArray(entries) || entries.length === 0) return null;
  return entries;
};

// roleOverride may be a single role string or a list of candidate roles. Composite
// pool shifts pass both the member's role and the shift's role so the comparison
// passes if EITHER matches. Resolve to an ordered, de-duplicated role list, falling
// back to the shift's own role when nothing usable is supplied.
const normalizeRoles = (roleOverride, shift) => {
  const list = (Array.isArray(roleOverride) ? roleOverride : [roleOverride]).filter(
    r => typeof r === 'string' && r,
  );
  if (list.length === 0 && typeof shift.role === 'string' && shift.role) list.push(shift.role);
  return [...new Set(list)];
};

// Status precedence so OR-ing over candidate roles keeps the strongest signal:
// a confirmed match beats a mismatch beats "no windows resolved" (null).
const idealRank = r => (r === 'match-individual' ? 3 : r === 'match-role' ? 2 : r === 'mismatch' ? 1 : 0);

// Returns { resolved, match } — `match` is true when some window contains the
// shift within buffer on both ends; `resolved` is true when any window's bounds
// were resolvable at all (distinguishes a genuine mismatch from "no windows").
const matchWindows = (windows, shift, shapeOfDay, buffer, role) => {
  let resolved = false;
  for (const [startBound, endBound] of windows) {
    const wStart = resolveBound(startBound, shift, shapeOfDay, role);
    const wEnd = resolveBound(endBound, shift, shapeOfDay, role);
    if (wStart == null || wEnd == null) continue;
    resolved = true;
    if (Math.abs(shift.start - wStart) <= buffer && Math.abs(shift.end - wEnd) <= buffer) {
      return { resolved: true, match: true };
    }
  }
  return { resolved, match: false };
};

const matchIdealForRole = (shift, member, config, shapeOfDay, buffer, role) => {
  const dayLower = shift.day.toLowerCase();
  const dayUpper = shift.day.toUpperCase();

  const individualWindows = getIndividualWindows(member, dayLower);
  const roleWindows = getRoleWindows(config, role, dayUpper);

  // Person-level windows win when they match, but a miss must fall back to the
  // role window — a valid role ideal shift should still get its tick even when
  // the person has (non-matching) historical windows for the day.
  const person = individualWindows
    ? matchWindows(individualWindows, shift, shapeOfDay, buffer, role)
    : { resolved: false, match: false };
  if (person.match) return 'match-individual';

  const roleRes = roleWindows
    ? matchWindows(roleWindows, shift, shapeOfDay, buffer, role)
    : { resolved: false, match: false };
  if (roleRes.match) return 'match-role';

  return person.resolved || roleRes.resolved ? 'mismatch' : null;
};

export const matchesIdealWindow = (shift, member, config, shapeOfDay, bufferHours = DEFAULT_BUFFER_HOURS, roleOverride = null) => {
  if (!shift || typeof shift.day !== 'string') return null;
  const buffer = Number.isFinite(bufferHours) ? bufferHours : DEFAULT_BUFFER_HOURS;
  let best = null;
  for (const role of normalizeRoles(roleOverride, shift)) {
    const r = matchIdealForRole(shift, member, config, shapeOfDay, buffer, role);
    if (idealRank(r) > idealRank(best)) best = r;
    if (best === 'match-individual') break;
  }
  return best;
};

const checkBoundary = (windows, shift, shapeOfDay, buffer, role) => {
  let resolved = false;
  let startOk = false;
  let endOk = false;
  for (const [startBound, endBound] of windows) {
    const wStart = resolveBound(startBound, shift, shapeOfDay, role);
    const wEnd = resolveBound(endBound, shift, shapeOfDay, role);
    if (wStart == null || wEnd == null) continue;
    resolved = true;
    if (Math.abs(shift.start - wStart) <= buffer) startOk = true;
    if (Math.abs(shift.end - wEnd) <= buffer) endOk = true;
    if (startOk && endOk) return { resolved: true, pass: true };
  }
  return { resolved, pass: startOk && endOk };
};

const boundaryRank = r =>
  r === 'boundary-individual' ? 3 : r === 'boundary-role' ? 2 : r === 'boundary-mismatch' ? 1 : 0;

const matchBoundaryForRole = (shift, member, config, shapeOfDay, buffer, role) => {
  const dayLower = shift.day.toLowerCase();
  const dayUpper = shift.day.toUpperCase();

  const personWindows = getIndividualWindows(member, dayLower);
  const roleWindows = getRoleWindows(config, role, dayUpper);

  const person = personWindows
    ? checkBoundary(personWindows, shift, shapeOfDay, buffer, role)
    : { resolved: false, pass: false };
  if (person.pass) return 'boundary-individual';

  const roleRes = roleWindows
    ? checkBoundary(roleWindows, shift, shapeOfDay, buffer, role)
    : { resolved: false, pass: false };
  if (roleRes.pass) return 'boundary-role';

  return person.resolved || roleRes.resolved ? 'boundary-mismatch' : null;
};

export const matchesBoundaryWindow = (
  shift,
  member,
  config,
  shapeOfDay,
  bufferHours = DEFAULT_BUFFER_HOURS,
  roleOverride = null,
) => {
  if (!shift || typeof shift.day !== 'string') return null;
  const buffer = Number.isFinite(bufferHours) ? bufferHours : DEFAULT_BUFFER_HOURS;
  let best = null;
  for (const role of normalizeRoles(roleOverride, shift)) {
    const r = matchBoundaryForRole(shift, member, config, shapeOfDay, buffer, role);
    if (boundaryRank(r) > boundaryRank(best)) best = r;
    if (best === 'boundary-individual') break;
  }
  return best;
};
